import type { DateTime } from "luxon";
import { useCallback } from "react";
import { useChosenDepartment } from "../contexts/ChosenDepartment";
import { calculateShiftTimes } from "../contexts/utils/shift-times";
import { type Day, NurseFullRole } from "../utils/models";
import { useRosterLookup } from "./useRosterLookup";

export type CalculateShiftTimes = (
	day: Pick<Day, "date">,
	shiftNo: number,
	nurseRole: NurseFullRole | undefined,
	location?: string,
	section?: string,
) => { startTime: DateTime; endTime: DateTime };

export type GetShiftTimes = (day: Day, shiftNo: number, nurseId: number, location?: string) => { start: DateTime; end: DateTime };

/** The department's default hours for a shift, without HospitalDataProvider: the provider's two helpers of the same names. */
export const useShiftTimes = (): { calculateShiftTimes: CalculateShiftTimes; getShiftTimes: GetShiftTimes } => {
	const { chosenDepartment } = useChosenDepartment();
	const { getNurseById } = useRosterLookup(chosenDepartment.department_id);

	const calculateDepartmentShiftTimes = useCallback<CalculateShiftTimes>(
		(day, shiftNo, nurseRole, location, section) => calculateShiftTimes({ day, shiftNo, nurseRole, chosenDepartment, location, section }),
		[chosenDepartment],
	);

	const getShiftTimes = useCallback<GetShiftTimes>(
		(day, shiftNo, nurseId, location) => {
			// DEFAULT: an RN's hours for a nurse not on the roster, as the provider's helper did.
			const nurseRole = getNurseById(nurseId)?.role ?? NurseFullRole.RN;
			const { startTime, endTime } = calculateDepartmentShiftTimes(day, shiftNo, nurseRole, location);
			return { start: startTime, end: endTime };
		},
		[getNurseById, calculateDepartmentShiftTimes],
	);

	return { calculateShiftTimes: calculateDepartmentShiftTimes, getShiftTimes };
};
